import { useState } from 'react';
import { Box, Paper } from '@mui/material';

import type { NormalizedModuleItem, SelectionKey } from '../../types/merge-customization';

import { CollapsedColumnStrip } from './CollapsedColumnStrip';
import { PatientColumnHeader } from './PatientColumnHeader';
import { PatientModuleCard } from './PatientModuleCard';
import { getPatternStyle } from './patientPatterns';

interface PatientColumnProps {
  patientId: string;
  patientName: string;
  isPrimary: boolean;
  /** Module data for this patient: Record<moduleName, items[]> */
  moduleData: Record<string, NormalizedModuleItem[]>;
  selections: Set<SelectionKey>;
  onToggleItem: (key: SelectionKey) => void;
  isDuplicate: (key: SelectionKey) => boolean;
  patientColor: string;
  patientIndex: number;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export function PatientColumn({
  patientId,
  patientName,
  isPrimary,
  moduleData,
  selections,
  onToggleItem,
  isDuplicate,
  patientColor,
  patientIndex,
  onSelectAll,
  onDeselectAll,
}: PatientColumnProps) {
  const [expanded, setExpanded] = useState(true);

  const allItems = Object.values(moduleData).flat();
  const selectedCount = allItems.filter(i => selections.has(i.selectionKey)).length;
  const totalCount = allItems.length;

  if (!expanded) {
    return (
      <CollapsedColumnStrip
        patientName={patientName}
        patientIdShort={`#${patientIndex + 1}`}
        selectedCount={selectedCount}
        totalCount={totalCount}
        patientColor={patientColor}
        patternStyle={getPatternStyle(patientIndex, patientColor)}
        onExpand={() => setExpanded(true)}
      />
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        width: 340,
        minWidth: 300,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <PatientColumnHeader
        patientName={patientName}
        patientIdShort={`#${patientIndex + 1}`}
        isPrimary={isPrimary}
        selectedCount={selectedCount}
        totalCount={totalCount}
        patientColor={patientColor}
        patternStyle={getPatternStyle(patientIndex, patientColor)}
        onSelectAll={onSelectAll}
        onDeselectAll={onDeselectAll}
        onCollapse={() => setExpanded(false)}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          p: 1.5,
          overflow: 'auto',
        }}
      >
        {Object.entries(moduleData).map(([moduleName, items]) => (
          <PatientModuleCard
            key={`${patientId}-${moduleName}`}
            moduleName={moduleName}
            items={items}
            selections={selections}
            onToggleItem={onToggleItem}
            isDuplicate={isDuplicate}
            patientColor={patientColor}
          />
        ))}
        {Object.keys(moduleData).length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>
            No module data
          </Box>
        )}
      </Box>
    </Paper>
  );
}
